// =============================================================================
// SIREPPAR - Bloque de "Antecedentes" dentro del cuerpo de un informe
// =============================================================================
// Mismo patrón que objecionAtributos.ts: al tildar intimaciones/observaciones en
// el panel lateral del modal, se agrega/actualiza un bloque gestionado al final
// del cuerpo del informe. El resto del texto se edita libremente; este bloque se
// reconstruye (idempotente) en cada cambio de selección, así la supervisora puede
// redactar arriba sin que se le pise lo escrito.
//
// El editor es Markdown (RichTextEditor → @uiw/react-md-editor), así que el bloque
// se arma en Markdown.
// =============================================================================

import { tipoDocLabels } from './tiposDocumento';
import { MARKER_ANTECEDENTES, aplicarBloqueGestionado } from './bloquesInforme';

export { MARKER_ANTECEDENTES };

export interface AdjuntoRespuestaInforme {
  id: string;
  tipo: string;              // TipoDocumento (se muestra con su label legible)
  titulo?: string;
  nombreOriginal?: string;
}

export interface ItemHistorialInforme {
  id: string;
  texto: string;
  tipo?: string;              // 'INTIMACION' | 'OBSERVACION' | …
  creadoEn: string;
  fechaLimite?: string;
  articulo?: string;
  respuestaTexto?: string;
  respuestaFecha?: string;
  /**
   * La Secretaría dio por cumplida la respuesta. NO es lo mismo que "respondida":
   * el partido pudo contestar algo que no alcanza. Es la separación que usa el panel
   * del informe (sin subsanar / subsanadas).
   */
  subsanada?: boolean;
  estado?: string;
  // Documentos que el partido acompañó al responder.
  adjuntos?: AdjuntoRespuestaInforme[];
}

const fmt = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

/**
 * Un ítem del historial como párrafo del informe: qué se pidió, qué respondió (o
 * no) el partido y qué documentación acompañó, con el nombre del tipo de
 * documento (no el nombre del archivo, que suele ser ilegible: "IMG_2831.pdf").
 */
export function bloqueItem(item: ItemHistorialInforme): string {
  const etiqueta = item.tipo === 'INTIMACION' ? 'Intimación' : 'Observación';
  // Estado de la observación en el encabezado, en dos palabras. El informe no lo
  // decía en ninguna parte: quien lo lee veía qué se pidió y qué contestó el
  // partido, pero no si la Secretaría lo dio por cumplido —que es lo único que
  // define si el requisito quedó satisfecho—. Va acá, y no como párrafo aparte,
  // porque es un dato de una palabra y no merece un renglón propio.
  const estado = item.subsanada ? 'subsanada' : 'sin subsanar';
  const encabezado =
    `**${etiqueta} del ${fmt(item.creadoEn)} — ${estado}**` +
    (item.articulo ? ` (${item.articulo})` : '') +
    (item.fechaLimite ? ` - plazo hasta el ${fmt(item.fechaLimite)}` : '');

  const partes = [encabezado, item.texto];

  if (item.respuestaTexto) {
    partes.push(`*Respuesta del partido (${fmt(item.respuestaFecha)}):* ${item.respuestaTexto}`);
  } else {
    partes.push('*El partido no presentó respuesta.*');
  }

  // Los adjuntos se listan aunque no haya texto de respuesta: el partido pudo
  // haber subido documentación sin escribir nada.
  if (item.adjuntos?.length) {
    const nombres = item.adjuntos.map((a) => tipoDocLabels[a.tipo] || a.titulo || a.tipo);
    partes.push(
      `*Documentación acompañada:* ${nombres.join(', ')}.`,
    );
  }

  return partes.join('\n\n');
}

/**
 * Reconstruye el bloque de antecedentes al final del cuerpo. `items` debe venir
 * ya ordenado (de la más antigua a la más reciente).
 */
export function aplicarAntecedentes(items: ItemHistorialInforme[], cuerpoActual: string): string {
  const bloque = items.length === 0
    ? ''
    : `${MARKER_ANTECEDENTES}\n\n${items.map(bloqueItem).join('\n\n---\n\n')}`;
  return aplicarBloqueGestionado(cuerpoActual, MARKER_ANTECEDENTES, bloque);
}
